import {createContext} from 'preact';
import {useCallback, useContext, useEffect, useState} from 'preact/hooks'
import {onAuthStateChanged} from 'firebase/auth'
import {auth} from "../includes/firebase.js";

const AuthContext = createContext({
    user: null,
    getToken: async () => null,
    authLoading: true,
})

const VERIFY_CHECK_MS = 60000;
let verifyCheck = {uid: null, at: 0, promise: null};

const checkVerified = (current) => {
    if (verifyCheck.uid !== current.uid || Date.now() - verifyCheck.at >= VERIFY_CHECK_MS) {
        verifyCheck = {
            uid: current.uid,
            at: Date.now(),
            promise: current.reload().then(() => current.emailVerified ? current.getIdToken(true) : null),
        };
    }
    return verifyCheck.promise;
};

export function AuthProvider({children}) {
    const [user, setUser] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (u) => {
            setUser(u ?? null)
            setAuthLoading(false);
        })
        return unsub
    }, [])

    const getToken = useCallback(async (forceRefresh = false) => {
        const current = auth.currentUser;
        if (!current) return null;
        // getIdToken can reject (e.g. offline refresh). Callers all handle a
        // null token already; a rejection thrown through their `await` would
        // skip the fetch chain's .finally and strand pending/loading state.
        try {
            const result = await current.getIdTokenResult(!!forceRefresh);
            if (result.claims.email_verified) return result.token;
            const fresh = await checkVerified(current).catch(() => null);
            return fresh ?? result.token;
        } catch (e) {
            return null;
        }
    }, [])

    return (
        <AuthContext.Provider value={{user, getToken, authLoading}}>
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => useContext(AuthContext)
